import { useEffect, useState } from 'react';
import { ProfileResponse, usePutProfilesMutation } from '../../store/backendApi';

interface UseProfileUpdateReturn {
  username: string;
  description: string;
  setUsername: (value: string) => void;
  setDescription: (value: string) => void;
  saveProfile: () => Promise<void>;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
}

/**
 * Hook to handle profile editing (username and description).
 * Platform-agnostic: Can be used in React Native.
 *
 * @param profile - The current profile data
 * @returns Profile form state, handlers, and save function
 */
export const useProfileUpdate = (profile?: ProfileResponse): UseProfileUpdateReturn => {
  const [username, setUsername] = useState('');
  const [description, setDescription] = useState('');
  const [updateProfile, { isLoading: isSaving }] = usePutProfilesMutation();

  // Sync local state with server profile data
  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setDescription(profile.description || '');
    }
  }, [profile]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = profile?.username !== username || profile?.description !== description;

  const saveProfile = async () => {
    await updateProfile({ username, description });
  };

  return {
    username,
    description,
    setUsername,
    setDescription,
    saveProfile,
    isSaving,
    hasUnsavedChanges,
  };
};
